if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
      }

      hide() {
        super.hide();
        // iOS Safari ignores overflow:hidden on body — undo the position:fixed
        // scroll lock from show() and restore the original scroll position.
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflowY = '';
        window.scrollTo(0, this._scrollY || 0);
        this.disconnectZoomBoundsObserver();
        if (this.mainSwiper && !this.mainSwiper.destroyed) this.mainSwiper.destroy(true, true);
        if (this.thumbSwiper && !this.thumbSwiper.destroyed) this.thumbSwiper.destroy(true, true);
        this.mainSwiper = null;
        this.thumbSwiper = null;
      }

      show(opener) {
        // iOS Safari ignores overflow:hidden on body — the position:fixed
        // trick is the reliable fix (mirrors FacetScrollLock in facets.js).
        this._scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this._scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.overflowY = 'scroll';
        super.show(opener);
        this.openedBy = opener;
        this.preloadAllImages();
        this.initSwiper();
        const cursor = this.querySelector('.custom-cursor');
        if (cursor) cursor.style.display = 'none';

        const images = this.querySelectorAll('.product__media-cell img');

        images.forEach((img) => {
          img.addEventListener('mouseenter', () => {
            cursor.style.display = 'block';
          });
          img.addEventListener('mousemove', (e) => {
            cursor.style.transform = `translate(${e.clientX - 12}px, ${e.clientY - 0.5}px)`;
          });
          img.addEventListener('mouseleave', () => {
            cursor.style.display = 'none';
          });
        });
      }

      initSwiper() {
        // Opened by a click, so Swiper is fetched here if the PDP gallery has not
        // already pulled it in.
        if (typeof Swiper === 'undefined') {
          window.loadSwiper().then(() => this.initSwiper());
          return;
        }

        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
        this.isDesktop = isDesktop;
        this.elements = {
          viewer: this.querySelector(isDesktop ? '[data-modal-viewer="desktop"]' : '[data-modal-viewer="mobile"]'),
          thumbnails: this.querySelector(isDesktop ? '[data-modal-thumbs="desktop"]' : '[data-modal-thumbs="mobile"]'),
        };

        // Initialize thumbnail swiper
        if (this.elements.thumbnails) {
          if (isDesktop) {
            this.thumbSwiper = new Swiper(this.elements.thumbnails, {
              direction: 'vertical',
              slidesPerView: 'auto',
              spaceBetween: 1,
              watchOverflow: true,
              freeMode: {
                enabled: true,
                sticky: false,
                momentumRatio: 0.5,
                momentumVelocityRatio: 0.5,
                momentumBounce: false,
              },
              watchSlidesProgress: true,
              mousewheel: {
                enabled: true,
                forceToAxis: true,
                releaseOnEdges: true,
              },
              allowTouchMove: true,
              navigation: {
                nextEl: this.elements.thumbnails.querySelector('.slider-button--next'),
                prevEl: this.elements.thumbnails.querySelector('.slider-button--prev'),
              },
            });
          } else {
            this.thumbSwiper = null;
          }

          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch, index) => {
            const button = mediaToSwitch.querySelector('button');
            if (!button) return;

            button.addEventListener('click', (event) => {
              event.preventDefault();
              const mediaId = mediaToSwitch.dataset.target;
              this.setActiveMedia(mediaId);
              this.updateActiveThumbnail(mediaId);
              if (this.thumbSwiper) this.thumbSwiper.slideTo(index);
            });

            mediaToSwitch.addEventListener('mouseenter', () => {
              const mediaId = mediaToSwitch.dataset.target;
              this.setActiveMedia(mediaId);
              this.updateActiveThumbnail(mediaId);
            });
          });
        }

        // Initialize main swiper
        this.mainSwiper = new Swiper(this.elements.viewer, {
          direction: 'vertical',
          slidesPerView: 1,
          spaceBetween: 0,
          freeMode: false,
          allowTouchMove: false,
          speed: 0,
          zoom: isDesktop ? false : { maxRatio: 5, minRatio: 2, toggle: false },
          mousewheel: {
            enabled: false,
          },
          keyboard: {
            enabled: false,
          },
          thumbs: this.thumbSwiper ? { swiper: this.thumbSwiper } : undefined,
          on: {
            init: (swiper) => {
              this.applyZoomLayout(swiper, swiper.slides[swiper.activeIndex], swiper.activeIndex);
            },
            slideChange: () => {
              const activeSlide = this.elements.viewer.querySelectorAll('.swiper-slide')[this.mainSwiper.activeIndex];
              if (!activeSlide) return;

              if (this.elements.thumbnails && this.thumbSwiper) {
                const { mediaId } = activeSlide.dataset;
                this.updateActiveThumbnail(mediaId);
              }

            },
            slideChangeTransitionEnd: () => {
              // Swiper can emit this event without the active slide actually
              // changing (e.g. an internal resize/update pass). Recentering or
              // resetting zoom on those spurious firings yanked the image back
              // to its resting position/scale mid pinch-zoom, so gate on the
              // index genuinely having changed.
              if (this._zoomedIndex === this.mainSwiper.activeIndex) return;
              this.applyZoomLayout(
                this.mainSwiper,
                this.mainSwiper.slides[this.mainSwiper.activeIndex],
                this.mainSwiper.activeIndex
              );
            },
          },
        });

        // Click on slide = close modal (both mobile and desktop).
        if (this.elements.viewer) {
          // A two-finger pinch can make the browser synthesize a trailing
          // "click" on release even though the user was zooming, not tapping
          // to close. Track whether the current touch sequence ever had a
          // second finger down and swallow the click it produces.
          let hadMultiTouch = false;
          this.elements.viewer.addEventListener(
            'touchstart',
            (event) => {
              if (event.touches.length === 1) hadMultiTouch = false;
              if (event.touches.length > 1) hadMultiTouch = true;
            },
            { passive: true }
          );
          this.elements.viewer.addEventListener('click', (event) => {
            if (hadMultiTouch) {
              hadMultiTouch = false;
              return;
            }
            if (event.target.closest('button, a, .slider-button, .swiper-pagination')) return;
            if (!event.target.closest('.swiper-slide')) return;
            this.hide();
          });
        }

        // Set initial slide to the opener media
        const openedMediaId = this.openedBy?.getAttribute('data-media-id');
        if (openedMediaId) {
          this.setActiveMedia(openedMediaId);
        } else {
          // No specific media, center the first slide
          const firstSlide = this.elements.viewer?.querySelector('.swiper-slide');
          if (firstSlide) this.centerZoomSlide(firstSlide);
        }

        // Sync initial active thumbnail
        const initialSlide = this.elements.viewer?.querySelectorAll('.swiper-slide')?.[this.mainSwiper.activeIndex];
        if (initialSlide?.dataset?.mediaId) this.updateActiveThumbnail(initialSlide.dataset.mediaId);

        // Hide cursor on hover of thumbnails and close button
        if (this.elements.thumbnails) {
          this.elements.thumbnails.addEventListener('mouseenter', () => {
            const cursor = this.querySelector('.custom-cursor');
            if (cursor) cursor.style.display = 'none';
          });
        }
        const closeButton = this.querySelector('.product-media-modal__toggle');
        if (closeButton) {
          closeButton.addEventListener('mouseenter', () => {
            const cursor = this.querySelector('.custom-cursor');
            if (cursor) cursor.style.display = 'none';
          });
        }

      }

      setActiveMedia(mediaId) {
        if (!this.mainSwiper) return;
        const slides = Array.from(this.elements.viewer.querySelectorAll('.swiper-slide'));
        const activeIndex = slides.findIndex((slide) => {
          if (slide.dataset.mediaId === mediaId) return true;
          const idList = slide.querySelector('[data-media-id-list]')?.dataset.mediaIdList || '';
          return idList ? idList.split(',').includes(mediaId) : false;
        });
        if (activeIndex !== -1) {
          this.mainSwiper.slideTo(activeIndex, 0);
          this.applyZoomLayout(this.mainSwiper, slides[activeIndex], activeIndex);
        }
      }

      // Shared by modal init, slide changes, and setActiveMedia — all three
      // need the same "size/center the zoom container, then zoom in on
      // mobile" sequence for whatever slide just became active.
      applyZoomLayout(swiper, activeSlide, index) {
        if (this.isDesktop) {
          this.centerZoomSlide(activeSlide);
          this._zoomedIndex = index;
          return;
        }
        this.syncZoomContainerBox(activeSlide, () => {
          this.enforceZoomBounds(activeSlide);
          if (swiper.zoom) {
            this.instantZoomIn(swiper);
          } else {
            this.centerZoomSlide(activeSlide);
          }
          this._zoomedIndex = index;
        });
      }

      updateActiveThumbnail(mediaId) {
        if (!this.elements.thumbnails) return;
        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        thumbnails.forEach((thumbnail) => {
          thumbnail.classList.toggle('is-active', thumbnail.dataset.target === mediaId);
        });

        if (this.thumbSwiper) {
          const idx = thumbnails.findIndex((thumbnail) => thumbnail.dataset.target === mediaId);
          if (idx >= 0) this.thumbSwiper.slideTo(idx, 200);
        }
      }

      instantZoomIn(swiper) {
        if (!swiper?.zoom) return;
        const activeSlide = swiper.slides[swiper.activeIndex];
        const wrap = this.getZoomElements(activeSlide)?.wrap;
        // Suppress transitions only for this programmatic reset — permanent
        // suppression breaks Swiper's pan momentum/bounce-back on touch release
        wrap?.classList.add('zoom-no-transition');
        // zoom.out() resets internal scale/translate so zoom.in re-centers at 0,0
        swiper.zoom.out();
        // Swiper's zoom.in(ratio) path (passing a bare number) can only resolve
        // a pan position from a real touch event's pageX/pageY. With no touch
        // in progress it computes NaN for the translate, which still gets
        // written into Swiper's internal x/y bookkeeping and corrupts the
        // anchor math for the next real pinch/pan — the image would zoom or
        // land somewhere other than where you touched. Capping maxRatio at
        // the ratio we want and calling zoom.in() with no argument keeps it on
        // the clean "centered, no forced ratio" code path instead, so the
        // internal state stays valid for whatever the user does next.
        const zoomParams = swiper.params.zoom;
        const originalMaxRatio = zoomParams.maxRatio;
        const targetScale = 2;
        zoomParams.maxRatio = targetScale;
        swiper.zoom.in();
        zoomParams.maxRatio = originalMaxRatio;
        // CSS transform scale doesn't change offsetHeight, so centerZoomSlide's
        // default (scale 1) centering is now wrong for the visually 2x-larger
        // image — recompute the same margin against the scaled size.
        this.centerZoomSlide(activeSlide, targetScale);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => wrap?.classList.remove('zoom-no-transition'));
        });
      }

      // Resolves the zoom container and image once so callers don't each
      // re-walk the same two-level DOM lookup.
      getZoomElements(slide) {
        const wrap = slide?.querySelector('.swiper-zoom-container');
        const img = wrap?.querySelector('img');
        return wrap && img ? { wrap, img } : null;
      }

      centerZoomSlide(slide, scale = 1) {
        const elements = this.getZoomElements(slide);
        if (!elements) return;
        const { wrap } = elements;
        // Swiper's zoom pan is a CSS transform on .swiper-zoom-container — it
        // has no idea the slide itself might be scrolled, so a scrollTop-based
        // centering and Swiper's transform-based centering disagree about
        // where "centered" is, and the image ends up rendered starting from
        // the top instead of the middle once scaled. Center with a margin
        // instead — a static layout offset that composes correctly with
        // Swiper's own translate.
        slide.scrollTop = 0;
        const gap = slide.clientHeight - wrap.offsetHeight * scale;
        // Once zoomed in, never shift the image upward to "center" it — that
        // crops it from the top. Only close a genuine gap (image still
        // shorter than the slide even at this scale); past that, stay flush
        // at the top and let any excess crop from the bottom instead.
        wrap.style.marginTop = `${scale > 1 ? Math.max(gap / 2, 0) : gap / 2}px`;
      }

      // The CSS sizes .swiper-zoom-container to the full slide height while the
      // <img> itself only renders at its natural aspect ratio (width:100%,
      // height:auto). Swiper's zoom module measures the touch/pinch anchor
      // against the container box, so that mismatch made it zoom into whatever
      // part of the image happened to line up with the touch point instead of
      // the part actually under your fingers. Setting an inline height on the
      // container — via JS, not the stylesheet — collapses it to the image's
      // real rendered box so Swiper's anchor math lines up with what's on screen.
      // onReady runs once the container's box actually matches the image,
      // synchronously if the image is already loaded or after its 'load'
      // event otherwise. Callers that center/zoom the slide right after
      // calling this — without waiting for onReady — were doing that math
      // against the OLD (full-slide-height) box whenever the image hadn't
      // finished loading yet, throwing off the centering (e.g. the top of
      // the image getting cut off on the very first zoom).
      syncZoomContainerBox(slide, onReady) {
        const done = () => onReady?.();
        const elements = this.getZoomElements(slide);
        if (!elements) return done();
        const { wrap, img } = elements;

        const applyHeight = () => {
          if (img.naturalWidth && img.naturalHeight) {
            const width = wrap.offsetWidth;
            if (width) wrap.style.height = `${(img.naturalHeight / img.naturalWidth) * width}px`;
          }
          done();
        };

        if (img.complete) {
          applyHeight();
        } else {
          img.addEventListener('load', applyHeight, { once: true });
        }
      }

      disconnectZoomBoundsObserver() {
        if (this._zoomBoundsObserver) {
          this._zoomBoundsObserver.disconnect();
          this._zoomBoundsObserver = null;
        }
      }

      // Swiper's own edge clamp while actively dragging is elastic — a
      // hardcoded rubber-band formula, not something exposed via params —
      // so it lets you drag a bit past the image edge before resistance
      // kicks in. Watching the container's transform and snapping it back
      // the instant it goes out of bounds turns that into a hard stop.
      enforceZoomBounds(slide) {
        this.disconnectZoomBoundsObserver();
        const elements = this.getZoomElements(slide);
        if (!elements) return;
        const { wrap, img } = elements;

        const clamp = () => {
          const scaleMatch = /scale\(([\d.]+)\)/.exec(img.style.transform || '');
          const scale = scaleMatch ? parseFloat(scaleMatch[1]) : 1;
          if (scale <= 1) return;

          const translateMatch = /translate3d\(\s*(-?[\d.]+)px,\s*(-?[\d.]+)px/.exec(wrap.style.transform || '');
          if (!translateMatch) return;
          const x = parseFloat(translateMatch[1]);
          const y = parseFloat(translateMatch[2]);

          const scaledWidth = img.offsetWidth * scale;
          const scaledHeight = img.offsetHeight * scale;
          const maxX = Math.max((scaledWidth - slide.offsetWidth) / 2, 0);
          const maxY = Math.max((scaledHeight - slide.offsetHeight) / 2, 0);
          const clampedX = Math.min(Math.max(x, -maxX), maxX);
          const clampedY = Math.min(Math.max(y, -maxY), maxY);

          if (clampedX !== x || clampedY !== y) {
            wrap.style.transform = `translate3d(${clampedX}px, ${clampedY}px,0)`;
          }
        };

        this._zoomBoundsObserver = new MutationObserver(clamp);
        this._zoomBoundsObserver.observe(wrap, { attributes: true, attributeFilter: ['style'] });
      }

      preloadAllImages() {
        const images = this.querySelectorAll('.product__media-cell img, .swiper-wrapper img');
        images.forEach((img) => {
          if (img.src && !img.complete) {
            const preloadImg = new Image();
            preloadImg.src = img.src;
            preloadImg.onload = () => {
              img.src = img.src; // Force reload if needed
            };
          }
        });
      }

      announceLiveRegion(activeItem, position) {
        // Implementation for accessibility
      }

      playActiveMedia(activeItem) {
        // Implementation for media playback
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
